import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import {
  Subject,
  first,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import {
  ProductDiscount,
  ProductDiscountService,
} from 'src/app/services/product-discount/product-discount.service';
import { UsersService } from 'src/app/services/users/users.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { CreateProductDiscountDialogComponent } from './create-product-discount-dialog/create-product-discount-dialog.component';
import { DeleteProductDiscountDialogComponent } from './delete-product-discount-dialog/delete-product-discount-dialog.component';
import { EditProductDiscountDialogComponent } from './edit-product-discount-dialog/edit-product-discount-dialog.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CurrencyPipe } from '@angular/common';

@Component({
  selector: 'app-product-discounts',
  templateUrl: './product-discounts.component.html',
  styleUrls: ['./product-discounts.component.scss'],
  imports: [
    PayeeButtonComponent,
    PayeeFormFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    MatProgressSpinner,
    CurrencyPipe,
  ],
})
export class ProductDiscountsComponent implements OnInit, OnDestroy {
  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private productDiscountService = inject(ProductDiscountService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private readonly destroy = new Subject<void>();

  get userName(): string {
    return this.usersService.userSubject.value?.name ?? '';
  }
  readonly dataSource = new MatTableDataSource<ProductDiscount>();
  readonly displayedColumns = [
    'product',
    'fee',
    'product_discountable_type',
    'product_discountable_name',
    'fee_before_discount',
    'discount',
    'fee_after_discount',
    'edit',
    'delete',
  ];
  readonly loading = signal(false);
  readonly claimAmount = new FormControl<number>(0, { nonNullable: true });

  pageSize: number = 10;
  pageIndex: number = 0;
  length?: number;

  async ngOnInit(): Promise<void> {
    this.claimAmount.valueChanges
      .pipe(takeUntil(this.destroy), distinctUntilChanged(), debounceTime(300))
      .subscribe((value) => {
        this.updateQueryParams(value);
        this.setDataSource();
      });

    // Load claim amount from query params
    const claimAmount = this.route.snapshot.queryParamMap.get('claimAmount');
    if (claimAmount) {
      const value = parseFloat(claimAmount);
      if (!isNaN(value)) {
        this.claimAmount.setValue(value);
      }
    }
    await this.setDataSource();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  createProductDiscount(): void {
    const ref = this.dialog.open(CreateProductDiscountDialogComponent);
    ref
      .afterClosed()
      .pipe(first(), takeUntil(this.destroy))
      .subscribe({
        next: async (result?: ProductDiscount) => {
          if (result) {
            this.dataSource.data = this.dataSource.data.concat(result);
          }
        },
      });
  }

  editProductDiscount(product: ProductDiscount): void {
    const ref = this.dialog.open(EditProductDiscountDialogComponent, {
      data: product,
    });
    ref
      .afterClosed()
      .pipe(first(), takeUntil(this.destroy))
      .subscribe({
        next: async (result?: ProductDiscount) => {
          if (result) {
            Object.assign(product, result);
          }
        },
      });
  }

  deleteProductDiscount(product: ProductDiscount): void {
    const ref = this.dialog.open(DeleteProductDiscountDialogComponent, {
      data: product,
    });
    ref
      .afterClosed()
      .pipe(first(), takeUntil(this.destroy))
      .subscribe({
        next: async (result?: ProductDiscount) => {
          if (result) {
            await this.setDataSource();
          }
        },
      });
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  async pageChange(event: PageEvent): Promise<void> {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    await this.setDataSource();
  }

  private updateQueryParams(claimAmount: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { claimAmount },
      queryParamsHandling: 'merge',
    });
  }

  private async setDataSource(): Promise<void> {
    try {
      this.loading.set(true);
      const claimAmount = this.claimAmount.value;
      const result = await this.productDiscountService.getProductDiscounts({
        page: this.pageIndex + 1,
        per_page: this.pageSize,
        claim_amount: claimAmount,
      });
      this.length = result.pagination.total;
      this.dataSource.data = result.data;
    } catch (error) {
      console.error('Error while loading product discounts', error);
    } finally {
      this.loading.set(false);
    }
  }
}
